/**
 * Shared color constants for TerraMind.
 * Use these instead of hardcoding hex values in components.
 * Palette: vibrant green (primary) + brown undertones (sidebar, accents)
 */

// Brand colors — from terramind.gr website
export const BRAND = {
  green:     '#5a8f3c',   // vibrant natural green (primary)
  greenDark: '#3d6b28',   // darker green for hover states
  greenLight:'#7cb342',   // lighter green
  lime:      '#8bc34a',   // lime green (CTAs, progress bars)
  gold:      '#c4a35a',   // gold/amber accent
  brownDark: '#3d2e1e',   // dark brown
  brownMed:  '#5c4a32',   // medium brown
  cream:     '#faf8f5',   // warm cream background
  beige:     '#f5f0e8',   // light section backgrounds
  sidebar:   '#2d4a2d',   // sidebar background (dark green, matches logo)
};

// Probe depths, shallow to deep: one palette for every readings chart (app and
// PDF, scripts/reports/report_charts.DEPTH_COLOR mirrors it; a test checks).
export const DEPTH_ORDER_COLORS = ['#3b82f6', '#2e7d32', '#f59e0b'];
// Irrigation limit line, irrigation runs and untrusted hours on charts.
export const CHART_MARKS = { limit: '#d32f2f', irrigation: '#1976d2', suspect: '#94a3b8' };

// Zone / field health state colors (derived from BRAND for consistency)
export const STATE_COLORS = {
  OK: BRAND.green,        // #5a8f3c
  MARGINAL: BRAND.gold,   // #c4a35a
  STRESSED: '#c0392b',
  SATURATED: '#0ea5e9',   // sky blue — over-watered
  UNKNOWN: '#9ca3af',
  WATERING: '#3d7cba',
};

// Convenience alias — fields use the same palette
export const FIELD_STATUS_COLORS = STATE_COLORS;
export const ZONE_STATE_COLORS = { ...STATE_COLORS, WATERING: '#3d7cba' };

// Control mode chip colors (AI = purple, MANUAL = neutral)
export const CONTROL_MODE_COLORS = {
  AI:     { bg: '#ede9fe', text: '#7c3aed', border: '#c4b5fd' },
  MANUAL: { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
};

// Schedule / activity accent colors
export const ACTIVITY_COLORS = {
  executing: '#3d7cba',
  pending:   '#7c3aed',
  completed: '#5a8f3c',
  queued:    '#c4a35a',
};

// Field map selection colors — high-contrast on satellite imagery
export const FIELD_SELECTION = {
  // White, not red: red on this map means «Πολύ ξηρό» (zone legend), so a
  // red selection read as an alarm (walk 2026-10-08). Selection shows by a
  // thicker line instead (MapShell).
  selected:     '#ffffff',   // selected field boundary / selected zone
  selectedFill: '#ffffff',   // fill color for selected field
  unselected:   '#ffffff',   // white outline for unselected field polygons
  zoneBorder:   '#ffffff',   // white zone stroke for contrast on satellite
};

// KPI card specific
export const KPI_COLORS = {
  primary: '#5a8f3c',
  info:    '#3d7cba',
  schedule:'#7c3aed',
  alert:   '#c0392b',
  neutral: '#9ca3af',
};

// VWC bar thresholds
export const VWC_COLORS = {
  low:    '#c0392b',  // < 20%
  medium: '#c4a35a',  // 20-35%
  good:   '#5a8f3c',  // > 35%
  empty:  '#e8e4de',
};

// Schedule mode colors
export const MODE_COLORS = {
  NORMAL:      { bg: '#f2f7f0', border: '#5a8f3c', text: '#4a7c3f' },
  FERTIGATION: { bg: '#faf5ff', border: '#7c3aed', text: '#6d28d9' },
  FROST:       { bg: '#eff6ff', border: '#3d7cba', text: '#2a5f8f' },
};

// Text colour hierarchy — use these instead of hardcoding greys
export const TEXT = {
  primary:     '#111827',   // headings, labels
  secondary:   '#6b7280',   // body secondary
  muted:       '#737a86',   // placeholders, captions: 4.4 to 1 on white, read outdoors
  disabled:    '#d1d5db',
  onDark:      '#ffffff',
  onDarkMuted: 'rgba(255,255,255,0.55)',
};

// Surface / background hierarchy
export const SURFACE = {
  base:        '#ffffff',
  subtle:      '#f9fafb',   // page background
  muted:       '#f3f4f6',   // card, chip backgrounds
  border:      '#e5e7eb',   // dividers, outlines
  borderDark:  '#d1d5db',
};

// Semantic status colours (alert severity + DSS)
export const STATUS = {
  error:       '#dc2626',
  errorBg:     '#fef2f2',
  errorText:   '#991b1b',
  warning:     '#f59e0b',
  warningBg:   '#fffbeb',
  warningText: '#92400e',
  success:     '#16a34a',
  successBg:   '#f0fdf4',
  successText: '#166534',
  info:        '#2563eb',
  infoBg:      '#eff6ff',
  infoText:    '#1d4ed8',
};

// Shared surface style for floating map overlays (layer pill, satellite controls,
// legend). Keeps radius / shadow / blur / border consistent across pills so the
// top-right, bottom-left, and top-center controls read as one visual family.
export const MAP_PILL_SX = {
  bgcolor: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
};
